/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
  writeBatch
} from "firebase/firestore";
import { nanoid } from "nanoid";

const state = {
  // Quiz data
  quiz: null,
  currentQuizHistory: [],

  // Loading states
  loadingQuiz: false,
  loadingQuizHistory: false,
  savingQuiz: false,
  processingQuiz: false,
  reorderingQuiz: false,

  // Quiz state management
  quizChanged: false,
  quizUpdates: {},

  // Error state
  error: null
};

const actions = {
  /**
   * Load a specific quiz by ID
   */
  async loadQuiz({ commit, getters }, quizId) {
    if (!quizId) {
      throw new Error("Quiz ID is required");
    }

    commit("setQuiz", null);
    commit("setLoadingQuiz", true);
    commit("setQuizError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizRef = doc(collection(programRef, "quizzes"), quizId);
      const quizDoc = await getDoc(quizRef);

      if (!quizDoc.exists()) {
        throw new Error("Quiz not found");
      }

      const data = quizDoc.data();
      const quiz = {
        id: quizDoc.id,
        ...data,
        created: data.created?.toDate() || new Date(),
        updated: data.updated?.toDate() || new Date()
      };
      commit("setQuiz", quiz);
      return quiz;
    } catch (error) {
      console.error("Error loading quiz:", error);
      commit("setQuizError", error.message);
      throw new Error("Error occurred when loading quiz");
    } finally {
      commit("setLoadingQuiz", false);
    }
  },

  /**
   * Load quiz history for a member
   */
  async loadQuizHistory({ commit, getters }, memberId) {
    console.log("loadQuizHistory for member:", memberId);
    if (!memberId) {
      console.error("Member ID is required");
      return [];
    }

    commit("setLoadingQuizHistory", true);
    commit("setQuizError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const historyRef = collection(programRef, "quizHistory");
      const historyQuery = query(
        historyRef,
        where("createdBy", "==", memberId),
        orderBy("created", "desc")
      );
      const querySnapshot = await getDocs(historyQuery);

      const history = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        history.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date()
        });
      });
      console.log("history for member:", memberId, history);
      commit("setCurrentQuizHistory", history);
      return history;
    } catch (error) {
      console.error("Error loading quiz history:", error);
      commit("setQuizError", error.message);
      commit("setCurrentQuizHistory", []);
      throw error;
    } finally {
      commit("setLoadingQuizHistory", false);
    }
  },

  /**
   * Create a new quiz
   */
  async createQuiz({ commit, getters, dispatch }, quizData) {
    if (!quizData || !quizData.title) {
      throw new Error("Quiz title is required");
    }

    commit("setSavingQuiz", true);
    commit("setQuizError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");

      // Check for duplicate titles
      const titleUppercase = quizData.title.toUpperCase();
      const duplicateQuery = query(
        quizzesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("A quiz with this title already exists");
      }

      // Calculate total points
      let totalPoints = 0;
      if (quizData.questions && Array.isArray(quizData.questions)) {
        quizData.questions.forEach(question => {
          if (question.answers && Array.isArray(question.answers)) {
            question.answers.forEach(answer => {
              totalPoints += answer.points || 0;
            });
          }
        });
      }

      const quiz = {
        ...quizData,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (quizData.passPoints || 0),
        status: quizData.status || "Draft",
        questions: quizData.questions || [],
        passPoints: quizData.passPoints || 0,
        passingScore: quizData.passingScore || 0
      };

      const newQuizRef = await addDoc(quizzesRef, quiz);

      const tempQuiz = {
        ...quiz,
        id: newQuizRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createQuiz", tempQuiz);
      dispatch("setSnackbar", "Quiz created successfully");

      return {
        success: true,
        quizId: newQuizRef.id,
        quiz: tempQuiz
      };
    } catch (error) {
      console.error("Error creating quiz:", error);
      commit("setQuizError", error.message);
      throw error;
    } finally {
      commit("setSavingQuiz", false);
    }
  },

  /**
   * Update an existing quiz
   */
  async updateQuiz({ commit, getters, dispatch }, payload) {
    if (!payload || !getters.quiz?.id) {
      throw new Error("Quiz data and current quiz are required");
    }

    commit("setSavingQuiz", true);
    commit("setReorderingQuiz", false);
    commit("setQuizError", null);

    try {
      const id = getters.quiz.id;
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");
      const quizRef = doc(quizzesRef, id);

      // Check for duplicate titles (excluding current quiz)
      if (payload.titleUppercase) {
        const duplicateQuery = query(
          quizzesRef,
          where("titleUppercase", "==", payload.titleUppercase)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error("A quiz with this title already exists");
        }
      }

      // Calculate total points
      let totalPoints = 0;
      if (payload.questions && Array.isArray(payload.questions)) {
        payload.questions.forEach(question => {
          if (question.answers && Array.isArray(question.answers)) {
            question.answers.forEach(answer => {
              totalPoints += answer.points || 0;
            });
          }
        });
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (payload.passPoints || 0)
      };

      await updateDoc(quizRef, updateData);

      const tempQuiz = {
        ...getters.quiz,
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (payload.passPoints || 0)
      };

      commit("updateQuiz", tempQuiz);
      dispatch("setSnackbar", "Quiz updated successfully");
      dispatch("resetQuizUpdates");

      return tempQuiz;
    } catch (error) {
      console.error("Error updating quiz:", error);
      commit("setQuizError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when updating quiz");
    } finally {
      commit("setSavingQuiz", false);
    }
  },

  /**
   * Delete a quiz
   */
  async deleteQuiz({ commit, getters, dispatch }, quizId) {
    if (!quizId) {
      throw new Error("Quiz ID is required");
    }

    commit("setQuizError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizRef = doc(collection(programRef, "quizzes"), quizId);

      await deleteDoc(quizRef);

      commit("deleteQuiz", quizId);
      dispatch("setSnackbar", "Quiz deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting quiz:", error);
      commit("setQuizError", error.message);
      throw new Error("Error when deleting the quiz");
    }
  },

  /**
   * Reorder quiz questions
   */
  async reorderQuizQuestions({ commit, state }, payload) {
    if (!payload || !state.quiz) {
      throw new Error("Quiz data is required for reordering");
    }

    // Make changes locally to show user visual changes immediately
    const update = {
      ...state.quiz,
      ...payload
    };

    commit("patchQuiz", update);
    commit("updateQuiz", update);
    commit("setQuizChanged", true);
  },

  /**
   * Handle quiz submission with comprehensive processing
   */
  async handleQuizSubmission({ getters, dispatch }, payload) {
    if (!payload || !payload.member) {
      throw new Error(
        "Quiz submission data with member information is required"
      );
    }

    dispatch("setProcessingQuiz", true);

    try {
      const batch = writeBatch(db());
      const programRef = doc(db(), "programs", getters.programId);

      // Save to quizHistory collection for reporting
      const quizData = {
        ...payload,
        created: serverTimestamp(),
        submittedAt: serverTimestamp(),
        id: nanoid()
      };

      const quizHistoryRef = doc(collection(programRef, "quizHistory"));
      batch.set(quizHistoryRef, quizData);

      // Award redeemable points if quiz was passed
      if (quizData.redeemable && quizData.passed) {
        const pointsQueueData = {
          ...quizData,
          type: "Award",
          processed: false,
          created: serverTimestamp()
        };
        const pointsQueueRef = doc(collection(programRef, "pointsQueue"));
        batch.set(pointsQueueRef, pointsQueueData);
      }

      // Handle databucket points if applicable
      if (quizData.databucket?.addToDatabucket && quizData.passed) {
        const databucketQuizData = {
          entryId: nanoid(),
          databucket: payload.databucket.databucketId,
          member: payload.member._id || payload.member.id,
          points: payload.points || 0,
          pointsSubArrayInDatabucket: payload.origin?.feature || "quiz",
          targetCode: payload.databucket.targetCode,
          uniqueIdentifier: Date.now(),
          created: new Date()
        };

        const dataBucketId = quizData.databucket.databucketId;
        const databucketRef = doc(
          collection(programRef, "databuckets"),
          dataBucketId
        );

        // Use transaction for databucket update to handle concurrent access
        await runTransaction(db(), async transaction => {
          const databucketDoc = await transaction.get(databucketRef);

          if (!databucketDoc.exists()) {
            throw new Error("Databucket does not exist");
          }

          const existingQuizzes = databucketDoc.data().quizzes || [];
          existingQuizzes.push(databucketQuizData);

          transaction.update(databucketRef, {
            quizzes: existingQuizzes,
            updated: serverTimestamp(),
            updatedBy: payload.member._id || payload.member.id
          });
        });
      }

      // Commit all batch operations
      await batch.commit();

      return {
        success: true,
        submissionId: quizHistoryRef.id,
        passed: quizData.passed,
        points: quizData.points || 0
      };
    } catch (error) {
      console.error("Error processing quiz submission:", error);
      throw new Error("Error processing quiz results: " + error.message);
    } finally {
      dispatch("setProcessingQuiz", false);
    }
  },

  /**
   * Get quiz by ID
   */
  async getQuizById({ getters }, quizId) {
    if (!quizId) {
      console.error("Quiz ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizRef = doc(collection(programRef, "quizzes"), quizId);
      const quizDoc = await getDoc(quizRef);

      if (quizDoc.exists()) {
        const data = quizDoc.data();
        return {
          id: quizDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Quiz with ID ${quizId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting quiz by ID:", error);
      return null;
    }
  },

  /**
   * Duplicate a quiz
   */
  async duplicateQuiz({ dispatch }, { quizId, newTitle }) {
    if (!quizId || !newTitle) {
      throw new Error("Quiz ID and new title are required");
    }

    try {
      const originalQuiz = await dispatch("getQuizById", quizId);
      if (!originalQuiz) {
        throw new Error("Original quiz not found");
      }

      const duplicatedQuizData = {
        ...originalQuiz,
        title: newTitle,
        titleUppercase: newTitle.toUpperCase(),
        status: "Draft"
      };

      // Remove ID and timestamp fields
      delete duplicatedQuizData.id;
      delete duplicatedQuizData.created;
      delete duplicatedQuizData.updated;

      const result = await dispatch("createQuiz", duplicatedQuizData);
      return result;
    } catch (error) {
      console.error("Error duplicating quiz:", error);
      throw error;
    }
  },

  // State management actions
  setProcessingQuiz({ commit }, payload) {
    commit("setProcessingQuiz", Boolean(payload));
  },

  setQuizChanged({ commit }, payload) {
    commit("setQuizChanged", Boolean(payload));
  },

  setSavingQuiz({ commit }, payload) {
    commit("setSavingQuiz", Boolean(payload));
  },

  patchQuiz({ commit }, payload) {
    commit("patchQuiz", payload);
  },

  resetQuizUpdates({ commit }) {
    commit("resetQuizUpdates", {});
  },

  setReorderingQuiz({ commit }, payload) {
    commit("setReorderingQuiz", Boolean(payload));
  },

  clearQuizError({ commit }) {
    commit("setQuizError", null);
  },

  clearCurrentQuiz({ commit }) {
    commit("setQuiz", null);
  },

  clearQuizHistory({ commit }) {
    commit("setCurrentQuizHistory", []);
  }
};

const mutations = {
  setQuiz(state, payload) {
    state.quiz = payload;
  },

  setCurrentQuizHistory(state, payload) {
    state.currentQuizHistory = payload;
  },

  setLoadingQuiz(state, payload) {
    state.loadingQuiz = Boolean(payload);
  },

  setLoadingQuizHistory(state, payload) {
    state.loadingQuizHistory = Boolean(payload);
  },

  setProcessingQuiz(state, payload) {
    state.processingQuiz = Boolean(payload);
  },

  setSavingQuiz(state, payload) {
    state.savingQuiz = Boolean(payload);
  },

  setReorderingQuiz(state, payload) {
    state.reorderingQuiz = Boolean(payload);
  },

  setQuizError(state, payload) {
    state.error = payload;
  },

  resetQuizUpdates(state, payload) {
    state.quizUpdates = { ...payload };
  },

  patchQuiz(state, payload) {
    state.quizUpdates = { ...payload };
  },

  setQuizChanged(state, payload) {
    state.quizChanged = Boolean(payload);
  },

  updateQuiz(state, payload) {
    state.quiz = payload;
  },

  createQuiz(state, payload) {
    state.quiz = payload;
  },

  deleteQuiz(state, quizId) {
    // Clear current quiz if it matches
    if (state.quiz && state.quiz.id === quizId) {
      state.quiz = null;
    }
  }
};

const getters = {
  /**
   * Get current quiz
   */
  quiz(state) {
    return state.quiz;
  },

  /**
   * Get current quiz history
   */
  currentQuizHistory(state) {
    return state.currentQuizHistory;
  },

  /**
   * Get quiz updates
   */
  quizUpdates(state) {
    return state.quizUpdates;
  },

  /**
   * Check if quiz has changed
   */
  quizChanged(state) {
    return state.quizChanged;
  },

  /**
   * Get loading states (renamed to avoid conflicts)
   */
  loadingQuiz(state) {
    return state.loadingQuiz;
  },

  loadingQuizHistory(state) {
    return state.loadingQuizHistory;
  },

  processingQuiz(state) {
    return state.processingQuiz;
  },

  quizSavingQuiz(state) {
    return state.savingQuiz;
  },

  reorderingQuiz(state) {
    return state.reorderingQuiz;
  },

  /**
   * Get error state
   */
  quizError(state) {
    return state.error;
  },

  /**
   * Check if any quiz operations are loading
   */
  quizIsLoading(state) {
    return (
      state.loadingQuiz ||
      state.loadingQuizHistory ||
      state.savingQuiz ||
      state.processingQuiz ||
      state.reorderingQuiz
    );
  },

  /**
   * Get quiz history count
   */
  quizHistoryCount(state) {
    return state.currentQuizHistory.length;
  },

  /**
   * Check if quiz history exists
   */
  hasQuizHistory(state) {
    return state.currentQuizHistory.length > 0;
  },

  /**
   * Get recent quiz history (last 10)
   */
  recentQuizHistory(state) {
    return state.currentQuizHistory.slice(0, 10);
  },

  /**
   * Get quiz statistics
   */
  quizStatistics(state) {
    const totalAttempts = state.currentQuizHistory.length;
    const passedAttempts = state.currentQuizHistory.filter(
      attempt => attempt.passed
    ).length;

    return {
      totalAttempts,
      passedAttempts,
      passRate:
        totalAttempts > 0
          ? ((passedAttempts / totalAttempts) * 100).toFixed(1)
          : 0
    };
  },

  /**
   * Get current quiz total possible points
   */
  currentQuizMaxPoints(state) {
    if (!state.quiz || !state.quiz.questions) {
      return 0;
    }

    let totalPoints = 0;
    state.quiz.questions.forEach(question => {
      if (question.answers && Array.isArray(question.answers)) {
        question.answers.forEach(answer => {
          totalPoints += answer.points || 0;
        });
      }
    });

    return totalPoints + (state.quiz.passPoints || 0);
  },

  /**
   * Check if quiz is valid for submission
   */
  isQuizValid(state) {
    return (
      state.quiz &&
      state.quiz.questions &&
      state.quiz.questions.length > 0 &&
      state.quiz.questions.every(
        question =>
          question.answers && question.answers.some(answer => answer.isCorrect)
      )
    );
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
